import React from 'react';
import { Box, Typography, Paper, useTheme } from '@mui/material';

/**
 * OfferCircle Component
 * Displays an offer as a circular element with title, divider, and points required
 *
 * @param {Object} props
 * @param {Object} props.offer - The offer data
 * @param {number} props.memberPoints - Current member points
 * @param {boolean} props.isOwned - Whether the member already has this offer
 * @param {boolean} props.isSelected - Whether the offer is currently selected
 */
const OfferCircle = ({ offer, memberPoints, isOwned, isSelected }) => {
  const theme = useTheme();

  // Determine if member has enough points for this offer
  const hasEnoughPoints = memberPoints >= (offer.points_required || 0);

  // Set up styles based on offer status
  const isAvailable = !isOwned && hasEnoughPoints;
  const isUnavailable = !isOwned && !hasEnoughPoints;

  // Determine border style
  let borderStyle = 'none';
  if (isSelected && (isAvailable || isOwned)) {
    borderStyle = `3px solid ${theme.palette.primary.main}`;
  }


  return (
    <Paper
      elevation={3}
      sx={{
        width: 200,
        height: 200,
        borderRadius: '50%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 1.5,
        margin: 1,
        opacity: isUnavailable ? 0.5 : 1,
        bgcolor: isOwned ? `${theme.palette.primary.main}10` : 'background.paper',
        border: borderStyle,
        transition: 'transform 0.3s ease',
        '&:hover': {
          transform: isUnavailable ? 'none' : 'scale(1.05)',
        },
        aspectRatio: '1 / 1', // Ensure it stays circular
        maxWidth: '90vw', // Prevent overflow on very small screens
      }}
    >
      {/* Offer title */}
      <Typography
        variant="body2"
        component="div"
        align="center"
        sx={{
          mt: { xs: 0.5, sm: 1 },
          height: '45px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.85rem',
          lineHeight: 1.2,
          px: 1,
        }}
      >
        {offer.name}
      </Typography>

      {/* Divider line (only shown for available/unavailable offers) */}
      <Box
        sx={{
          width: '80%',
          height: '1px',
          bgcolor: 'divider',
          my: { xs: 0.5, sm: 1 }
        }}
      />

      {/* Points required text (only shown for available/unavailable offers) */}
      <Typography
        variant="subtitle1"
        color="text.secondary"
        align="center"
        component="div"
        sx={{
          mb: 0.5,
          height: '45px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.7rem'
        }}
      >
        {isOwned ? <b>Redeemable</b> : <>Requires {offer.point_value} points</>}
      </Typography>
    </Paper>
  );
};

export default OfferCircle;